import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { DataProvider } from "react-admin";
import { config } from "../config";
import {
  BaseDataProvider,
  DataProviderParams,
  DataProviderResult,
} from "./base/BaseDataProvider";
import { UsersDataProvider } from "./providers/UsersDataProvider";
import {
  ChallengesDataProvider,
  ChallengeUpdatesDataProvider,
} from "./providers/ChallengesDataProvider";
import { ChatDataProvider } from "./providers/ChatDataProvider";
import {
  SubscriptionPlansDataProvider,
  SubscriptionLimitersDataProvider,
  UserSubscriptionsDataProvider,
  SubscriptionStatsDataProvider,
  LimiterStatsDataProvider,
} from "./providers/SubscriptionsDataProvider";
import {
  SupplementsDataProvider,
  SupplementOrdersDataProvider,
  PaymentTransactionsDataProvider,
} from "./providers/SupplementsDataProvider";
import { NotificationsDataProvider } from "./providers/NotificationsDataProvider";
import { AuthServiceDataProvider } from "./providers/AuthServiceDataProvider";

// Создаем Apollo Client
const httpLink = createHttpLink({
  uri: config.graphqlEndpoint,
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem("token");
  console.log("🔑 [Apollo] Auth token:", token ? "Present" : "Missing");
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

export class MainDataProvider implements DataProvider {
  private providers: Map<string, BaseDataProvider | any> = new Map();
  private authServiceProvider: AuthServiceDataProvider;

  constructor() {
    // Auth Service Provider (REST API, not GraphQL)
    this.authServiceProvider = new AuthServiceDataProvider();

    // Инициализируем все провайдеры
    this.providers.set("users", new UsersDataProvider(client));
    this.providers.set("challenges", new ChallengesDataProvider(client));
    this.providers.set(
      "challengeUpdates",
      new ChallengeUpdatesDataProvider(client),
    );
    this.providers.set("chatMessages", new ChatDataProvider(client));
    this.providers.set(
      "subscriptionPlans",
      new SubscriptionPlansDataProvider(client),
    );
    this.providers.set(
      "subscriptionLimiters",
      new SubscriptionLimitersDataProvider(client),
    );
    this.providers.set(
      "userSubscriptions",
      new UserSubscriptionsDataProvider(client),
    );
    this.providers.set(
      "subscriptionStats",
      new SubscriptionStatsDataProvider(client),
    );
    this.providers.set("limiterStats", new LimiterStatsDataProvider(client));
    this.providers.set("supplements", new SupplementsDataProvider(client));
    this.providers.set(
      "supplementOrders",
      new SupplementOrdersDataProvider(client),
    );
    this.providers.set(
      "paymentTransactions",
      new PaymentTransactionsDataProvider(client),
    );
    this.providers.set("notifications", new NotificationsDataProvider(client));
  }

  private getProvider(resource: string): BaseDataProvider | any {
    // Check if it's an auth service resource
    if (resource === "auth-users" || resource === "oauth-clients") {
      return this.authServiceProvider;
    }

    const provider = this.providers.get(resource);
    if (!provider) {
      throw new Error(`No provider found for resource: ${resource}`);
    }
    return provider;
  }

  async getList(resource: string, params: any): Promise<DataProviderResult> {
    return this.getProvider(resource).getList(resource, params);
  }

  async getOne(
    resource: string,
    params: { id: string | number },
  ): Promise<DataProviderResult> {
    return this.getProvider(resource).getOne(resource, {
      id: String(params.id),
    });
  }

  async getMany(
    resource: string,
    params: { ids: (string | number)[] },
  ): Promise<DataProviderResult> {
    return this.getProvider(resource).getMany(resource, {
      ids: params.ids.map(String),
    });
  }

  async getManyReference(
    resource: string,
    params: any,
  ): Promise<DataProviderResult> {
    return this.getProvider(resource).getManyReference(resource, params);
  }

  async create(
    resource: string,
    params: { data: any },
  ): Promise<DataProviderResult> {
    return this.getProvider(resource).create(resource, { data: params.data });
  }

  async update(
    resource: string,
    params: { id: string | number; data: any },
  ): Promise<DataProviderResult> {
    return this.getProvider(resource).update(resource, {
      id: String(params.id),
      data: params.data,
    });
  }

  async updateMany(
    resource: string,
    params: { ids: (string | number)[]; data: any },
  ): Promise<DataProviderResult> {
    return this.getProvider(resource).updateMany(resource, {
      ids: params.ids.map(String),
      data: params.data,
    });
  }

  async delete(
    resource: string,
    params: { id: string | number; previousData?: any },
  ): Promise<DataProviderResult> {
    return this.getProvider(resource).delete(resource, {
      id: String(params.id),
      previousData: params.previousData,
    });
  }

  async deleteMany(
    resource: string,
    params: { ids: (string | number)[] },
  ): Promise<DataProviderResult> {
    return this.getProvider(resource).deleteMany(resource, {
      ids: params.ids.map(String),
    });
  }

  // Специальные методы для уведомлений
  async getNotificationStats(): Promise<any> {
    const notificationsProvider = this.providers.get(
      "notifications",
    ) as NotificationsDataProvider;
    if (!notificationsProvider) {
      throw new Error("Notifications provider not found");
    }
    return notificationsProvider.getNotificationStats();
  }

  async getQueueStats(): Promise<any> {
    const notificationsProvider = this.providers.get(
      "notifications",
    ) as NotificationsDataProvider;
    if (!notificationsProvider) {
      throw new Error("Notifications provider not found");
    }
    return notificationsProvider.getQueueStats();
  }

  async getQueueDetails(limit: number = 20): Promise<any> {
    const notificationsProvider = this.providers.get(
      "notifications",
    ) as NotificationsDataProvider;
    if (!notificationsProvider) {
      throw new Error("Notifications provider not found");
    }
    return notificationsProvider.getQueueDetails(limit);
  }

  async cleanupOldNotifications(daysOld: number = 30): Promise<number> {
    const notificationsProvider = this.providers.get(
      "notifications",
    ) as NotificationsDataProvider;
    if (!notificationsProvider) {
      throw new Error("Notifications provider not found");
    }
    return notificationsProvider.cleanupOldNotifications(daysOld);
  }
}

// Создаем и экспортируем экземпляр
export const dataProvider = new MainDataProvider();
